import type { JSONContent } from "@tiptap/core";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { XOR } from "../../types.ts";
import { z } from "zod";
import type { RFQListQuery } from "../../components/RFQ/List/useRFQListFilters.tsx";
import { objectToQueryParams } from "../../util/logic.ts";
import type { RFQReminder } from "./RFQReminders.ts";
import type { Label } from "./Labels.ts";
import type { User } from "./Auth.ts";
import type { FileUpload } from "./FileUpload.ts";
import type { FileSourceItem } from "./FileLayout.ts";
import type { RFQCommunication, RFQUncategorizedCommunication } from "./RFQMailbox.ts";
import type { DraftCommunication, UpdateDraftCommunicationParams } from "./Mailbox.ts";
import type { AttributeValue } from "./Attributes.ts";
import type { AttributeFilter } from "../../components/Attributes/types.ts";

// Matches the statuses returned from the backend
export const RFQStatusSchema = z.enum(["IN_PROGRESS", "DRAFT", "CLOSED", "ARCHIVED"]);
export type RFQStatus = z.infer<typeof RFQStatusSchema>;

export enum RFQStatuses {
    IN_PROGRESS = "IN_PROGRESS",
    DRAFT = "DRAFT",
    CLOSED = "CLOSED",
    ARCHIVED = "ARCHIVED",
}

// Meant to replicate the Prisma schema type in purchaser-api/prisma/schema.prisma
export type RFQ = {
    id: number;
    uniquePublicId: string;
    createdAt: string;
    updatedAt: string; // RFQ database entity updatedAt
    deletedAt: string | null;
    archivedAt: string | null;
    title: string | null;
    content: string;
    contentJson: JSONContent;
    subject: string;
    requestSummary: string;
    submissionDeadline: string | null;
    rfqExtractionStartedAt: string | null; // ISO date string, set when RFQ line item extraction begins

    // Relationships
    teamId: number;
    // team
    createdByUserId: number;
    createdByUser: User;
    assignedToUserId: number;
    assignedToUser: User;
    senderId: number;
    sender: UserEmailSender;

    rfqAttachments: RFQAttachment[];
    rfqReminders: RFQReminder[];
    labels: Label[];

    // TODO: this is any right now because the final structure isn't decided upon
    comparisons: any[];
    rfqSubscriptions: RfqSubscription[];
    tables: RFQTabular[];

    // Computed by the backend
    status: RFQStatus;
    hasUnread: boolean;
    quoteCount: number;
    messageCount: number;
    vendorCount: number; // count of unique vendorRelationshipIds
    contacts: { email: string; vendorRelationshipId: number }[]; // Unique list of contacts for the RFQ, derived from stagedEmails

    lastActivityAt: string; // last relevant change to anything related to the RFQ

    // Computed by Prisma
    emailAddress: string;
    requisitionId: number | null;
};

export type RFQListLabel = {
    id: number;
    name: string;
    color: string;
    groupId: number | null;
};

export type RFQListEntry = {
    uniquePublicId: string;
    createdAt: string;
    updatedAt: string;
    title: string | null;
    createdByUserDisplayName: string;
    createdByUserId: number;
    assignedToUserDisplayName: string;
    assignedToUserId: number;
    status: RFQStatus;
    hasUnread: boolean;
    quoteCount: number;
    messageCount: number;
    awardCount: number;
    labels: RFQListLabel[];

    vendorCount: number; // count of unique vendorRelationshipIds
    lastActivityAt: string; // last relevant change to anything related to the RFQ
};

// More than this is returned but seem to be the only fields used
type NewRFQResponse = {
    uniquePublicId: string;
    globalUniqueId: string;
    emailAddress: string;
    subject: string | null;
};

export type VendorRelationship = {
    id: number;
    createdAt: string;
    deletedAt: string | null;
    updatedAt: string;
    name: string | null;
    attributeValues: AttributeValue[];
    contacts: { id: number; email: string; teamEmailAddressId: number }[]; // minimal Contact
    files?: VendorRelationshipFile[];
};

export type VendorRelationshipFile = {
    id: number;
    file: {
        id: number;
        originalFilename: string;
        internalFilename: string;
        fileSizeBytes: number;
        mimeType: string;
    };
};

export type Contact = {
    email: string;
    id: number;
    teamEmailAddressId: number;
    vendorName?: string | null;
    vendorRelationshipId: number;
    vendorRelationship: VendorRelationship;
    attributeValues: AttributeValue[];
    lists: ShortContactList[];

    // Not used anywhere (yet)
    createdAt: string;
    createdBy: number;
    deletedAt: string | null;
    updatedAt: string;
};

export type ShortContactList = {
    id: number;
    name: string;
};

export interface UserEmailSender {
    id: number;
    email?: string;
    emailEngineAccountId: string;
    isShared: boolean;
    enhancedRouting: boolean;
    createdAt: string;
    updatedAt: string;
    userId: string;
    teamId: string;
    // TODO: Needs type
    emailEngineData?: any;
}

export interface UserEmailSenderSignature extends UserEmailSender {
    signature: string;
}

export type RFQAttachment = {
    id: number;
    createdAt: string;
    updatedAt: string;
    fileUploadId: number;
    rfqId: number;
    file: FileUpload;
};

export type RfqQuote = {
    id: number;
    /** Set once the quote has been read for citations, whether or not any were found. */
    citationsCompletedAt?: string | null;
    read: boolean;
    summary: string;
    grandTotal?: number;
    grandTotalMetadata?: {
        confidence: number; // 0-100
        calculationMethod?: string; // How the value was derived (incl. MathTool operations)
        // Whether the total was explicitly stated ("defined"), inferred from line items ("inferred"), or not found ("none").
        basis?: "defined" | "inferred" | "none";
        // True when a charge (freight/shipping) is listed without a price — total likely incomplete.
        hasUnpricedCharges?: boolean;
        // True when the total should be verified by a human before it's trusted (low confidence, unpriced freight, or no total found).
        needsReview?: boolean;
        // Legacy fields; retained for rows written before 2026-07-06 but no longer emitted by purchaser-api.
        defined?: number; // Explicitly stated total
        inferred?: number; // Calculated total
        /** document_memory ids, matching FileSourceItem.sourceId. May be an earlier revision's document. */
        factIds?: number[];
    };
    currencyCode?: string;
    currencyCodeMetadata?: {
        defined?: string; // Explicitly stated currency
        inferred?: string; // Inferred from context
        confidence: number; // 0-100
        /** document_memory ids, matching FileSourceItem.sourceId. May be an earlier revision's document. */
        factIds?: number[];
    };
    validUntil?: string;
    validUntilMetadata?: {
        type: "ABSOLUTE" | "RELATIVE";
        originalText: string; // Original quote text (e.g., "valid for 30 days")
        confidence: number; // 0-100
        calculatedFrom?: string; // ISO timestamp used as reference (for RELATIVE dates)
        /** document_memory ids, matching FileSourceItem.sourceId. May be an earlier revision's document. */
        factIds?: number[];
        /** Facts the quote date in calculatedFrom was read out of. */
        quoteDateFactIds?: number[];
    };
    leadTime?: string;
    leadTimeMetadata?: {
        confidence: number; // 0-100
        reasoning?: string; // Explanation of classification
        /** document_memory ids, matching FileSourceItem.sourceId. May be an earlier revision's document. */
        factIds?: number[];
    };
    quoteNumber?: string;
    quoteNumberMetadata?: {
        confidence: number; // 0-100
        /** document_memory ids, matching FileSourceItem.sourceId. May be an earlier revision's document. */
        factIds?: number[];
    };
    createdAt: string;
    emailAttachment: {
        file: FileUpload;
    };
};

// purchaser-api/services/ai/RFQv2/GenerateRFQFromSingleFile.ts
type PatchRFQResult = {
    headers: string[];
    rows: string[][];
};

export type RFQListStats = {
    totalRFQs: number;
    statusCounts: {
        status: string;
        count: number;
    }[];
    userCreatedCounts: {
        userId: number;
        userDisplayName: string;
        rfqCount: number;
    }[];
    userAssignedCounts: {
        userId: number;
        userDisplayName: string;
        rfqCount: number;
    }[];
    dateRangeCounts: {
        today: { startTime: number; createdAtCount: number; updatedAtCount: number };
        week: { startTime: number; endTime: number; createdAtCount: number; updatedAtCount: number };
        month: { startTime: number; endTime: number; createdAtCount: number; updatedAtCount: number };
        ytd: { startTime: number; endTime: number; createdAtCount: number; updatedAtCount: number };
    };
};

export type RFQListResponse = {
    stats: RFQListStats;
    myRFQsStatusCounts: { status: string; count: number }[];
    results: RFQListEntry[];
    pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

export type RFQTabular = {
    id: number;
    rfqId: number;
    rfq: RFQ;
    tabularDataStoreId: number;
    tabularDataStore: Tabular;
};

export type RevisionSourceAttribute = {
    /** quoteDate is the date printed on the quote, cited separately from when it expires. */
    field: "leadTime" | "grandTotal" | "validUntil" | "quoteDate" | "currencyCode" | "quoteNumber";
    factIds: number[];
};

export type RevisionSourceFile = {
    fileId: number;
    /** The file itself, so nothing has to resolve the id against something else. */
    file: FileUpload;
    /** The message this file arrived on. Null when it reached the team on another thread. */
    communicationId: number | null;
    /** The revision this file first contributed in; null alongside a null communicationId. */
    establishedIn: number | null;
    /** Carried forward from a revision earlier than the one asked about. */
    inherited: boolean;
    attributes?: RevisionSourceAttribute[];
};

export type ContributingCommunication = {
    communicationId: number;
    revision: number;
    inherited: boolean;
    /** Every attribute this message established, from its body and its attachments alike. */
    fields: RevisionSourceAttribute["field"][];
    /** Citations read out of the message body. Empty when its values came from attachments. */
    bodyAttributes: RevisionSourceAttribute[];
    /** The stored .eml those body citations point into. Null when nothing was read out of the body. */
    bodyFile: FileUpload | null;
};

/** One fact a quote's values cite, with the file it lives in. */
export type RevisionSourceFact = FileSourceItem & {
    /** The text the value was read from, word for word. The summary paraphrases it, so only this
     *  matches an email body well enough to highlight. */
    rawContext: string;
    fileId: number;
};

export type RevisionSources = {
    revision: number;
    /**
     * The attachments this revision's values were read out of, earlier ones still cited included.
     * A message body is never here; it is reported on its communication instead. Falls back to the
     * revision message's own attachments when the quote cites nothing.
     */
    documents: RevisionSourceFile[];
    /** Every fact the quote's values cite, from attachments and message bodies alike. */
    citedFactIds: number[];
    /** The cited facts themselves, keyed by fact id. Keyed rather than nested under the attributes
     *  because one fact commonly backs several values. */
    facts: Record<number, RevisionSourceFact>;
    communications: ContributingCommunication[];
};

export type MailboxTabular = {
    id: number;
    vendorName: string;
    // Optional because the tabular API doesn't return it; populated
    // client-side at the fetch sites that need it for vendor navigation
    // (e.g. the vendor-name menu in the quote views). Older fetch paths
    // that only need revision counts can omit it.
    vendorRelationshipId?: number;
    mailboxId: number;
    communicationId: number;
    communication?: RFQCommunication;
    changedFields: string[];
    revision: number;
    table: Tabular;
    type: "LINE_ITEMS";
};

export type Tabular = {
    columnDefinitions: TabularColumnDefinition[];
    rows: TabularRow[];
    createdAt: string;
    updatedAt: string;
    description: string;
    id: number;
    name: string;
    /** Per-vendor "ignore" decisions from the semantic-column classifier. Cells render
     *  "N/A" with the reason instead of an empty cell when a concept doesn't apply to
     *  this vendor's product domain. Optional — null when no ignore decisions exist or
     *  for stores that predate the classifier (e.g. RFQ-side stores). */
    ignoredSemanticColumns?: {
        system?: Record<string, { reason: string; confidence: number }>;
        team?: Record<string, { reason: string; confidence: number }>;
        rfq?: Record<string, { reason: string; confidence: number }>;
    } | null;
};

export type TabularColumnDefinition = {
    id: string;
    dataType: "text" | "number" | "date" | "currency";
    hidden: boolean;
    name: string;
    order: number;
    /**
     * Semantic columns the classifier mapped this vendor column to. A single column can carry
     * multiple concepts at once — e.g. a "Price / linear foot" column reads as both UNIT_PRICE
     * (system) and a team-scoped price_per_linear_foot, and the buyer's views for either
     * should resolve against the same cell. Unset if the classifier didn't pick `map` for
     * this column on any concept. Each entry is a structured { scope, key } ref.
     */
    semanticColumns?: { scope: "system" | "team" | "rfq"; key: string }[];
};

/**
 * A value for a semantic column that was derived from an unstructured source rather than a
 * directly-mapped vendor column. Carries provenance so the UI can flag it as derived and offer
 * a tooltip showing the exact substring it came from.
 */
export type DerivedSemanticValue = {
    value: string;
    source: string;
    sourceQuote?: string;
};

/**
 * Per-row derived values, split by semantic column scope. Inner maps are keyed by the raw
 * semantic column key (no prefix encoding). Any sub-map may be absent when that scope had
 * no derived values on the row.
 */
export type DerivedSemanticValues = {
    system?: Record<string, DerivedSemanticValue>;
    team?: Record<string, DerivedSemanticValue>;
    rfq?: Record<string, DerivedSemanticValue>;
};

/**
 * Snapshot of one input the executor saw when computing a calculated value, captured per row
 * so the sidebar can show "this used UNIT_PRICE = $4.20 (mapped from column 'Unit Price')."
 *
 * Discriminated union by `kind`. Two variants today:
 *   - "semantic_ref": a registry-defined column (system/team/rfq) the planner can portably
 *     reference across vendors. Carries normalized unit + provenance.
 *   - "vendor_column": a raw vendor column the planner referenced as a fallback when no
 *     semantic ref carried the signal. Vendor-shaped — works only for vendors with a
 *     column of that name.
 *   - "rfq_column": a buyer-authoritative cell from the RFQ-side row this vendor row was
 *     matched to. Used for cross-side calcs (discrepancy, "% over our target") where the
 *     RFQ value is part of the answer rather than a stand-in for missing vendor data.
 *
 * Future variants land as additional kinds without reshaping existing ones.
 */
export type CalculatedInputSnapshot =
    | {
          kind: "semantic_ref";
          ref: { scope: "system" | "team" | "rfq"; key: string };
          value: string;
          unit: string;
          source: "mapped" | "derived" | "calculated";
      }
    | {
          kind: "vendor_column";
          columnName: string;
          value: string;
      }
    | {
          kind: "rfq_column";
          /** Buyer-authoritative cell from the RFQ-side row this vendor row was matched to.
           *  Distinct from vendor_column so the cell sidebar can label it as coming from the
           *  RFQ rather than the vendor — important for cross-side calcs (discrepancy,
           *  "% over our target") where the buyer needs to see which side each input came from. */
          columnName: string;
          value: string;
      };

/**
 * One step of an executed plan, recorded for sidebar display so the buyer can see the work
 * the LLM authored and the deterministic executor performed.
 */
export type ExecutedPlanStep = {
    op: string;
    args: Record<string, string | number>;
    outputLabel: string;
    outputValue: string;
    outputUnit: string;
    /** Optional human-readable explanation of where the step's value or operation came from.
     *  Surfaced in the cell sidebar so a buyer can audit numeric constants chosen by the
     *  planner (e.g. "2x4 nominal lumber: 2*4/12 = 0.6667 board-feet per linear foot"). */
    rationale?: string;
};

/**
 * The recorded result of running a calculated column's plan against a single row. Inlined on
 * the row so the cell sidebar can render full provenance without an extra fetch. The full
 * abstract plan lives in calculated_value_plan referenced by planId.
 */
export type CalculatedSemanticValue = {
    /** One-sentence plain-English description of what the plan does, authored by the
     *  planner. Surfaces at the top of the cell sidebar so a buyer can read the
     *  calculation pattern without parsing each step. */
    humanSummary?: string;
    value: string;
    unit: string;
    status: "ok" | "unable";
    reason?: string;
    inputs: CalculatedInputSnapshot[];
    steps: ExecutedPlanStep[];
    computedAt: string;
};

/**
 * Per-row calculated values, split by recipe scope. No "system" bucket because the system
 * registry is read-only and exclusively resolved-type.
 */
export type CalculatedSemanticValues = {
    team?: Record<string, CalculatedSemanticValue>;
    rfq?: Record<string, CalculatedSemanticValue>;
};

export type TabularRow = {
    id: number;
    order: number;
    cells: TabularRowCell[];
    /** Derived values for semantic columns that have no direct column match on this row. */
    derivedSemanticValues?: DerivedSemanticValues | null;
    /** Calculated values for this row, scope-split. Only present once the calculate step has
     *  produced a value for the column. Cells with a defined-but-not-yet-computed column
     *  render a "calculating…" affordance until this populates. */
    calculatedSemanticValues?: CalculatedSemanticValues | null;
};

export type TabularRowCell = {
    columnId: string;
    columnName: string;
    value: string | number | Date | Array<any>;
    dataType: "text" | "number" | "date" | "currency" | "object";
    hidden: boolean;
};

// Compliance Report Types
export type CheckStatus = "PASS" | "FAIL" | "SKIPPED";
export type ItemStatus = "PASS" | "WARNING" | "FAIL";
export type PrimaryIssue = "None" | "SPEC_DEVIATION" | "UOM_MISMATCH" | "QTY_VARIANCE" | "LOGISTICS_VARIANCE" | "NO_BID";

export type ComplianceCategories = {
    spec_check: CheckStatus;
    uom_check: CheckStatus;
    qty_check: "PASS" | "FAIL"; // qty_check cannot be SKIPPED
    logistics_check: CheckStatus;
};

export type ComplianceEvidence = {
    rfq_row_id: string;
    rfq_column_id: string;
    rfq_value: string;
    quote_value: string;
};

export type ComplianceItem = {
    rfq_id: string;
    status: ItemStatus;
    categories: ComplianceCategories;
    primary_issue: PrimaryIssue;
    confidence: number; // 0-100
    analysis: string;
    evidence: ComplianceEvidence;
};

export type ComplianceSummary = {
    total_lines_scanned: number;
    total_issues_found: number;
    severity_counts: {
        critical: number;
        warning: number;
    };
    category_counts: {
        spec_deviation: number;
        qty_variance: number;
        uom_mismatch: number;
        logistics_variance: number;
        no_bid: number;
    };
};

export type ComplianceReportData = {
    summary: ComplianceSummary;
    compliance_report: ComplianceItem[];
};

export type ComplianceStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";

export type ComplianceReport = {
    id: number;
    status: ComplianceStatus;
    reportData: ComplianceReportData;
    createdAt: string;
    errorMessage?: string;
    modelUsed?: string;
};

export type ListRecipient = {
    type: "LIST";
    id: number;
    contactCount: number;
    name: string;
    score: number; // float
};

export type ContactRecipient = {
    type: "CONTACT";
    id: number;
    email: string;
    teamEmailAddressId: number;
    vendorRelationshipId: number;
    vendorName: string | null;
    score: number; // float
};

export type RecipientItem = XOR<ListRecipient, ContactRecipient>;

export type FilteredRecipientVendor = {
    vendorRelationshipId: number;
    vendorName: string | null;
    contacts: { id: number; email: string; teamEmailAddressId: number }[];
    totalContactCount: number;
    matchedContactCount: number;
};

export type FilteredRecipientsResponse = {
    vendors: FilteredRecipientVendor[];
    totalVendors: number;
    totalContacts: number;
};

export type RfqSubscription = {
    id: number;
    createdAt: string;
    updatedAt: string;
    userId: number;
    rfqId: number;
    user: User;
};

// From @types/routes/rfq/index.ts in purchaser-api
const RFQPatchSchema = z.object({
    title: z
        .string()
        .nullish()
        .transform(val => (val === null ? null : val?.trim() || undefined))
        .optional(),
    content: z.string().nullish(),
    contentJson: z.json().nullish(),
    subject: z
        .string()
        .nullish()
        .transform(val => (val === null ? null : val?.trim() || undefined))
        .optional(),
    senderId: z.number().nullish(),
    archive: z.boolean().nullable().optional(),
});

export type RFQPatch = z.infer<typeof RFQPatchSchema>;

export type VendorRfqCheckResult = {
    results: Record<string, { contactOnRfqs: string[]; vendorOnRfqs: string[] }>;
};

export interface BounceContactCreate {
    mutation: "create";
    email: string;
    originalEmail: string;
}
export interface BounceContactUpdate {
    mutation: "update";
    contactId: number;
    email: string;
    originalEmail: string;
}
export interface BounceContactRemove {
    mutation: "remove";
    contactId?: number;
    originalEmail: string;
}
export type BounceUpdate = BounceContactCreate | BounceContactUpdate | BounceContactRemove;

export default {
    getRFQList: async (filters?: RFQListQuery): Promise<RFQListResponse> => {
        // attributeFilters must be appended via string concatenation rather than through
        // objectToQueryParams + URLSearchParams.toString(). objectToQueryParams pre-encodes
        // arrays of objects with encodeURIComponent, and then URLSearchParams.toString()
        // encodes the result a second time. The backend's decodeQueryString only decodes once,
        // leaving still-encoded JSON that fails JSON.parse. Building the string manually
        // ensures encodeURIComponent is applied exactly once.
        const { attributeFilters, ...restFilters } = filters ?? {};
        const parts: string[] = [];
        const qs = objectToQueryParams(restFilters).toString();
        if (qs) parts.push(qs);
        if (attributeFilters?.length) parts.push(`attributeFilters=${encodeURIComponent(JSON.stringify(attributeFilters))}`);
        const queryString = parts.length ? "?" + parts.join("&") : "";

        const url = PURCHASER_API_BASE_URL + "/v2/rfqs" + queryString;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            console.error("Failed to get RFQs", res);
            throw new Error("Failed to get RFQs");
        }
        return (await res.json()) as RFQListResponse;
    },

    getRFQv2: (publicId: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}`, { headers: getHeaders() }).then(res => res.json()) as Promise<RFQ>,

    sendRFQv2: (publicId: string, mailboxIds: number[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/send`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ mailboxIds }),
        }).then(res => res.json()),

    updateRFQ: async (params: { uniquePublicId: string; payload: RFQPatch; keepalive?: boolean }) => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/${params.uniquePublicId}`;
        // keepalive lets the request complete after the page starts unloading (exit flush on
        // reload/close). Body is capped at ~64KB for keepalive requests.
        const res = await fetch(url, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(params.payload),
            keepalive: params.keepalive,
        });

        if (!res.ok || res.status > 399) {
            throw new Error(`Failed to update RFQ with response: ${JSON.stringify(res)}`);
        }
    },

    updateRFQStatus: (publicId: string, status: RFQStatus) => {
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/change-status/${status}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update RFQ status");
            }
            return res.json();
        });
    },

    archiveRFQv2: (publicId: string, archive: boolean) => {
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ archive }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update archive status on RFQ");
            }
            return res.json();
        });
    },

    createRFQv2: (title = "", contentJson = {}): Promise<NewRFQResponse> => {
        /**
         * This method will create a new RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({
                title,
                content: "",
                contentJson,
            }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ");
            }
            return res.json();
        });
    },

    duplicateRFQ: (publicId: string, options: { includeSubject: boolean; includeAttachments: boolean }): Promise<NewRFQResponse> => {
        // Title and content are always copied; the options gate the optional extras. The server does
        // the whole copy (subject, attachments) in one request.
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/duplicate`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(options),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to duplicate RFQ");
            }
            return res.json();
        });
    },

    deleteRFQ: (uniquePublicId: string): Promise<void> => {
        const { "Content-Type": _, ...headersWithoutContentType } = getHeaders();
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfq/${uniquePublicId}`, {
            method: "DELETE",
            headers: headersWithoutContentType,
        }).then(res => {
            if (!res.ok) throw new Error("Failed to delete RFQ");
        });
    },

    generateSubjectLine: (publicId: string) => {
        /**
         * This method will generate a subject line for the RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/generate-subject`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to generate subject line for RFQ");
            }
            return res.json();
        });
    },

    assignUserToRFQ: (publicId: string, userId: number | null) => {
        if (userId === undefined) throw new Error("Invalid userId");
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/assign-user`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ userId }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to assign user to RFQ");
            }
            const json = await res.json();
            return json;
        });
    },

    // Set submission deadline for an RFQ.
    // null can be passed to remove the deadline.
    setRFQSubmissionDeadline: async (publicId: string, submissionDeadline: Date | null) => {
        const formattedDeadline = submissionDeadline ? submissionDeadline.toISOString() : null;
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/set-submission-deadline`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ submissionDeadline: formattedDeadline }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to set RFQ submission deadline");
            }
            const json = await res.json();
            return json;
        });
    },

    /**
     * Uses a file to create quick RFQ boilerplate data and optionally add the file as an attachment - must upload
     * file first and get a fileUploadId
     */
    patchRFQWithFile: async (rfqPublicId: string, fileUploadId: number, attachFile: boolean): Promise<PatchRFQResult> => {
        return await fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/from-document`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ rfqPublicId, fileUploadId, attachFile }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ from document");
            }
            const json = await res.json();
            return json as PatchRFQResult;
        });
    },

    createRFQFromTemplate: (templateId: number, title = "") =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/from-template`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({
                templateId,
                title,
            }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ from template");
            }
            const json = await res.json();
            return json;
        }),

    patchRFQFromTemplate: (rfqPublicId: string | number, templateId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/from-template`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({
                // The API requires a string, but the UI code makes it a number in some
                // instances. This alleviates the ambiguity.
                rfqPublicId: rfqPublicId.toString(),
                templateId,
            }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to patch RFQ from template");
            }
            const json = await res.json();
            return json;
        }),

    attachFilesToRFQ: (publicId: string, fileUploadIds: number[]) => {
        /**
         * This method will attach files to the RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/attachments`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ fileUploadIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to attach files to RFQ");
            }
            return res.json();
        });
    },

    detachFilesFromRFQ: (publicId: string, fileUploadIds: number[]) => {
        /**
         * This method will detach files from the RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/attachments`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ fileUploadIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to detach files from RFQ");
            }
            return res.json();
        });
    },

    getAttachmentDownloadLink: (mailboxId: number, communicationId: number, attachmentId: number) => {
        /**
         * This method returns a url and expiration date for a specific mailbix and communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/communications/${communicationId}/attachments/${attachmentId}/download`, {
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                console.error(`Attempt to generate attachment download link failed`, { mailboxId, communicationId, attachmentId });
                throw new Error("Unable to generate download link for attachment.");
            }

            return res.json();
        });
    },

    getUncategorizedAttachmentDownloadLink: (uncategorizedCommunicationId: number, attachmentId: number) => {
        const url = `${PURCHASER_API_BASE_URL}/uncategorized/${uncategorizedCommunicationId}/attachments/${attachmentId}/download`;
        return fetch(url, { headers: getHeaders() }).then(res => {
            if (!res.ok) {
                console.error(`Attempt to generate uncategorized attachment download link failed`, { uncategorizedCommunicationId, attachmentId });
                throw new Error("Unable to generate download link for attachment.");
            }

            return res.json();
        });
    },

    replyToMailbox: (mailboxId: number, draftCommunicationId: number) => {
        /**
         * This method will reply to a mailbox with the given content
         * of the draft.
         */
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/communications`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ draftCommunicationId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to reply to RFQ mailbox");
            }
            return res.json();
        });
    },

    broadcastToAllMailboxes: (rfqPublicId: string, draftCommunicationId: number, mailboxIds?: number[]) => {
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/broadcast`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ draftCommunicationId, ...(mailboxIds?.length ? { mailboxIds } : {}) }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to reply to RFQ mailbox");
            }
            return res.json();
        });
    },

    getUncategorizedCommunications: async (rfqUniquePublicId: string): Promise<RFQUncategorizedCommunication[]> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/uncategorized?rfqUniquePublicId=${rfqUniquePublicId}`, { headers: getHeaders() });
        const json = await res.json();
        return json;
    },

    associateUncategorizedCommunicationWithExistingMailbox: (id: number, mailboxId: number) => {
        /**
         * This method will associate a communication with a mailbox
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}/mailbox`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ mailboxId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to associate communication with mailbox");
            }
            return res.json();
        });
    },

    associateUncategorizedCommunicationWithNewMailbox: (id: number, rfqUniquePublicId: string) => {
        /**
         * This method will associate a communication with a new mailbox
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}/mailbox/new`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ rfqUniquePublicId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to associate communication with new mailbox");
            }
            return res.json();
        });
    },

    moveUncategorizedCommunicationToNewRFQ: (id: number, rfqUniquePublicId: string) => {
        /**
         * This method will associate a communication with a mailbox
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}/move`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ rfqUniquePublicId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to associate communication with mailbox");
            }
            return res.json();
        });
    },

    deleteUncategorizedCommunication: (id: number) => {
        /**
         * This method will delete a communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to delete communication");
            }
        });
    },

    /**
     * Mark a communication as read.  If you do this on an outgoing message, the API will throw an error.
     * @param communicationId
     */
    setCommunicationRead: (communicationId: number) => {
        return fetch(`${PURCHASER_API_BASE_URL}/communications/${communicationId}/mark-as-read`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to mark communication as read");
            }
            return res.json();
        });
    },

    createDraftCommunication: (
        rfqUniquePublicId: string,
        contentJson: JSONContent,
        mailboxId?: number | null,
        senderId?: number | null,
    ): Promise<DraftCommunication | undefined> => {
        /**
         * This method will create a draft communication for an RFQ
         * and mailbox (optional)
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ rfqUniquePublicId, mailboxId, contentJson, senderId }),
        }).then(res => {
            // If we get a 409 Conflict that means the draft already exists
            if (res.status === 409) return;

            if (!res.ok) {
                throw new Error("Failed to create draft communication");
            }
            return res.json();
        });
    },

    getDraftCommunication: (id: number) => {
        /**
         * This method will fetch a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}`, {
            method: "GET",
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to fetch draft communication");
            }
            return res.json();
        });
    },

    getDraftCommunicationByRfqMailbox: (rfqUniquePublicId: string, mailboxId?: number | null): Promise<DraftCommunication | null> => {
        /**
         * This method will fetch a draft communication based on
         * the RFQ and mailbox (optional). If mailbox is not passed
         * in, that indicates a broadcast communication draft.
         */
        return fetch(
            `${PURCHASER_API_BASE_URL}/draft-communications?rfqUniquePublicId=${rfqUniquePublicId}${mailboxId ? `&mailboxId=${mailboxId}` : ""}`,
            {
                method: "GET",
                headers: getHeaders(),
            },
        ).then(res => {
            if (!res.ok) {
                return null;
            }
            return res.json();
        });
    },

    getDraftCommunicationByMailbox: (mailboxId: number): Promise<DraftCommunication | null> => {
        /**
         * Fetch the current user's draft for a mailbox directly (RFQ or standalone PO).
         * Mailbox-scoped counterpart to getDraftCommunicationByRfqMailbox.
         */
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/draft-communications`, {
            method: "GET",
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                return null;
            }
            return res.json();
        });
    },

    createDraftCommunicationForMailbox: (
        mailboxId: number,
        contentJson: JSONContent,
        senderId?: number | null,
    ): Promise<DraftCommunication | undefined> => {
        /**
         * Create a draft for a mailbox directly (RFQ or standalone PO). Mailbox-scoped
         * counterpart to createDraftCommunication (which requires an RFQ).
         */
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/draft-communications`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ contentJson, senderId }),
        }).then(res => {
            // A 409 Conflict means the draft already exists.
            if (res.status === 409) return;

            if (!res.ok) {
                throw new Error("Failed to create draft communication");
            }
            return res.json();
        });
    },

    updateDraftCommunication: (params: UpdateDraftCommunicationParams) => {
        /**
         * This method will update the content of a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${params.id}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(params),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update draft communication");
            }
            return res.json();
        });
    },

    deleteDraftCommunication: (id: number) => {
        /**
         * This method will delete a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to delete draft communication");
            }
            return res.json();
        });
    },

    addFilesToDraftCommunication: (id: number, fileIds: number[]) => {
        /**
         * This method will add uploaded files as attachments
         * to a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}/attachments`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ fileIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to add files to draft communication");
            }
            return res.json();
        });
    },

    removeFileFromDraftCommunication: (id: number, attachmentId: number) => {
        /**
         * This method will delete an attachment on a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}/attachments/${attachmentId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to remove file from draft communication");
            }
            return res.json();
        });
    },

    // Get populated draft recipients for a draft communication
    getDraftRecipients: async (draftCommunicationId: number): Promise<{ emailRecipients: string[] }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${draftCommunicationId}/recipients`, {
            headers: getHeaders(),
        });
        if (!res.ok) throw new Error("Failed to get draft recipients");
        return res.json();
    },

    // Add a recipient to a draft communication
    addDraftRecipient: async (draftCommunicationId: number, emailAddress: string): Promise<{ emailRecipients: string[] }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${draftCommunicationId}/recipients`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ emailAddress }),
        });
        if (!res.ok) throw new Error("Failed to add draft recipient");
        return res.json();
    },

    // Remove a recipient from a draft communication
    removeDraftRecipient: async (draftCommunicationId: number, emailAddress: string): Promise<{ emailRecipients: string[] }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${draftCommunicationId}/recipients`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ emailAddress }),
        });
        if (!res.ok) throw new Error("Failed to remove draft recipient");
        return res.json();
    },

    getRfqTabularData: async (rfqPublicId: string) => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/tables`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!res.ok) throw new Error("Failed to get RFQ tabular data");
        return res.json() as Promise<{ tables: Tabular[] }>;
    },

    getRevisionSources: async (mailboxId: number | string, tableId: number) => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/tables/${tableId}/sources`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!res.ok) {
            throw new Error("Failed to get revision sources");
        }
        return res.json() as Promise<RevisionSources>;
    },

    getMailboxTabularData: async (mailboxId: string, includeCommunication = false) => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/tables?includeCommunication=${includeCommunication}`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!res.ok) {
            throw new Error("Failed to get mailbox tabular data");
        }
        return res.json() as Promise<{ data: MailboxTabular[] }>;
    },

    // this is how you get the actual rows for a revision
    getMailboxTabularDataRevision: async (mailboxId: string, revision = "latest") => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/tables/revisions/${revision}`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!res.ok) {
            throw new Error("Failed to get mailbox tabular data");
        }
        return res.json() as Promise<MailboxTabular>;
    },

    updateTabularField: async (tabularId: number, rowId: number, columnId: string, value: any): Promise<{ failed: any[]; successful: any[] }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/tabular-data/${tabularId}/rows/edit`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify({
                rows: [
                    {
                        id: rowId,
                        cells: [{ columnId, value: value }],
                    },
                ],
            }),
        });
        if (!res.ok) {
            throw new Error("Failed to update tabular field");
        }
        return res.json();
    },

    updateTabularColumnDefinition: (tabularId: string, columnId: string, name: string, hidden: boolean) => {
        return fetch(`${PURCHASER_API_BASE_URL}/tabular-data/${tabularId}/column-definitions`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify([{ id: columnId, name, hidden }]),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update tabular column definition");
            }
            return res.json();
        });
    },

    setRFQQuoteRead: (rfqQuoteId: number) => {
        return fetch(`${PURCHASER_API_BASE_URL}/rfq-quotes/${rfqQuoteId}/mark-as-read`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to mark communication as read");
            }
            return res.json();
        });
    },

    deleteTabularRows: async (tabularId: number, rowIds: number[]): Promise<void> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/tabular-data/${tabularId}/rows`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ rowIds }),
        });
        if (!res.ok) {
            throw new Error("Failed to delete tabular rows");
        }
    },

    // TExclude is used as a conditional type check to ensure that the return type is correct based on the excludeLists parameter.
    // If excludeLists is true, the return type will be ContactRecipient[], otherwise it will be RecipientItem[].
    // https://www.typescriptlang.org/docs/handbook/2/conditional-types.html
    getRecipientOptions: async <TExclude extends boolean = false>({
        search,
        limit,
        offset,
        excludeLists,
    }: {
        search?: string;
        limit?: number;
        offset?: number;
        excludeLists?: TExclude;
    }): Promise<{ recipients: TExclude extends true ? ContactRecipient[] : RecipientItem[] }> => {
        let params: string[] = [];
        if (search) params.push(`search=${encodeURIComponent(search)}`);
        if (limit) params.push(`limit=${limit}`);
        if (offset) params.push(`offset=${offset}`);
        if (excludeLists) params.push(`excludeLists=true`);
        const res = await fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/recipients${params.length > 0 && "?" + params.join("&")}`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get recipient options");
        }
        return res.json() as Promise<{ recipients: TExclude extends true ? ContactRecipient[] : RecipientItem[] }>;
    },

    resendAfterBounce: (mailboxId: number, communicationId: number, contactUpdates: BounceUpdate[]) => {
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/communications/${communicationId}/resend-after-bounce`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ contactUpdates }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to resend communication after bounce");
            }
            return res.json();
        });
    },

    /**
     * Get the compliance report for a mailbox.
     * Returns null if no compliance report exists.
     * @param mailboxId - The mailbox ID
     * @param mailboxTableId - Optional mailbox table ID to get compliance for a specific revision
     */
    getMailboxComplianceReport: async (mailboxId: string, mailboxTableId?: number): Promise<ComplianceReport | null> => {
        const params = mailboxTableId ? `?mailboxTableId=${mailboxTableId}` : "";
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/compliance${params}`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (res.status === 404) {
            return null;
        }
        if (!res.ok) {
            throw new Error("Failed to get compliance report");
        }
        return res.json();
    },

    /**
     * Trigger generation of a compliance report for a mailbox.
     * Returns the pending compliance record.
     */
    generateMailboxComplianceReport: async (mailboxId: string): Promise<ComplianceReport> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/compliance/generate`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });
        if (!res.ok) {
            throw new Error("Failed to generate compliance report");
        }
        return res.json();
    },

    checkVendorOnRfqs: async (emails: string[]): Promise<VendorRfqCheckResult> => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/vendor-check?emails=${encodeURIComponent(emails.join(","))}`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to check vendor RFQ associations");
        return await res.json();
    },

    /**
     * Export quote view data (Side by Side or Compliance) as Excel or CSV
     * @param publicId - The RFQ unique public ID
     * @param viewType - The view type: "side-by-side" or "compliance"
     * @param format - The export format: "xlsx" or "csv"
     * @param vendorColumns - Optional vendor column configuration for side-by-side view
     */
    exportQuoteView: async (
        publicId: string,
        viewType: "side-by-side" | "compliance",
        format: "xlsx" | "csv" = "xlsx",
        vendorColumns: { mailboxId: number; mailboxTableId: number; vendorName: string; activeColumnId: string; activeColumnName: string }[],
    ): Promise<Blob> => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/export?view=${viewType}&format=${format}&vendorColumns=${encodeURIComponent(JSON.stringify(vendorColumns))}`;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            throw new Error("Failed to export quote view");
        }
        return await res.blob();
    },

    /**
     * Export a full multi-sheet RFQ workbook (Overview, RFQ Items, Side by Side, List View, Compliance, per-vendor sheets)
     * @param publicId - The RFQ unique public ID
     */
    exportFullRfqWorkbook: async (publicId: string): Promise<Blob> => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/export?view=full-workbook&format=xlsx`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to export full RFQ workbook");
        return await res.blob();
    },

    /**
     * Export mailbox-specific quote view data (Side by Side or Compliance) as Excel or CSV
     * @param mailboxId - The mailbox ID
     * @param viewType - The view type: "side-by-side" or "compliance"
     * @param format - The export format: "xlsx" or "csv"
     * @param mailboxTableId - The specific revision's table ID to export
     */
    exportMailboxQuoteView: async (
        mailboxId: string | number,
        viewType: "side-by-side" | "compliance",
        format: "xlsx" | "csv" = "xlsx",
        mailboxTableId: number,
    ): Promise<Blob> => {
        const url = `${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/export?view=${viewType}&format=${format}&mailboxTableId=${mailboxTableId}`;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            throw new Error("Failed to export mailbox quote view");
        }
        return await res.blob();
    },

    getFilteredRecipients: async (params: {
        vendorAttributeFilters?: AttributeFilter[];
        contactAttributeFilters?: AttributeFilter[];
        listIds?: number[];
        limit?: number;
        offset?: number;
    }): Promise<FilteredRecipientsResponse> => {
        const queryParams = objectToQueryParams(params);
        const res = await fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/potential-recipients?${queryParams.toString()}`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get filtered recipients");
        }
        return await res.json();
    },
};
